import React, { FunctionComponent } from "react";
import "./SubscribedUser.scss";
import Table from "../../Components/Table/Table";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import TableSearch from "../../Components/Table/components/TableSearch";
import { Columns } from "../../Components/Table/ITable";
import { Button, ButtonGroup, Menu, MenuItem, CircularProgress, Box } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { useEffect } from "react";
import { listSubscribedUser } from "../../Redux/Slices/Package/ListSubscribedUserSlice";
import dayjs from "dayjs";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { IconButton } from "@mui/material";
import { useState } from "react";


import { ISubscribedUser } from "./ISubscribedUser";

interface DownloadButtonProps {
    data: any[];
}

const DownloadButton: FunctionComponent<DownloadButtonProps> = ({ data }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDownloadExcel = () => {
        // Prepare data for Excel (flattened)
        const exportData = data.map((row) => ({
            "S.No": row.index,
            Username: row.username,
            Email: row.email,
            "Year Group": row.year_group,
            Package: row.package_name,
            "Start Date": row.subscribed_on,
            "End Date": row.end_date,
            "Pending Days": row.pending_days,
            "Contact No": row.phone_no,
            Price: row.amount,
            Status: row.status ? "Active" : "Inactive",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Subscribed Users");
        XLSX.writeFile(workbook, "SubscribedUsers.xlsx");
        handleClose();
    };

    const handleDownloadCSV = () => {
        const exportData = data.map((row) => ({
            "S.No": row.index,
            Username: row.username,
            Email: row.email,
            "Year Group": row.year_group,
            Package: row.package_name,
            "Start Date": row.subscribed_on,
            "End Date": row.end_date,
            "Pending Days": row.pending_days,
            "Contact No": row.phone_no,
            Price: row.amount,
            Status: row.status ? "Active" : "Inactive",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", "SubscribedUsers.csv");
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        handleClose();
    };



    return (
        <React.Fragment>
            <ButtonGroup
                variant="contained"
                aria-label="split button"
                sx={{ height: "30px" }}
            >
                <Button
                    onClick={handleDownloadExcel}
                    startIcon={<DownloadIcon />}
                    sx={{
                        backgroundColor: "#009dff",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#64b5f6" },
                    }}
                >
                    Download
                </Button>
                <Button
                    size="small"
                    aria-controls={open ? "split-button-menu" : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-label="select merge strategy"
                    aria-haspopup="menu"
                    onClick={handleClick}
                    sx={{
                        color: "#fff",
                        "&:hover": { backgroundColor: "#64b5f6" },
                    }}
                >
                    <ArrowDropDownIcon sx={{ color: "#fff" }} />
                </Button>
            </ButtonGroup>
            <Menu
                id="split-button-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    "aria-labelledby": "split-button-menu",
                }}
            >
                <MenuItem onClick={handleDownloadCSV}>Export as CSV</MenuItem>

            </Menu>
        </React.Fragment>
    );
};

const SubscribedUser: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const { data: subscribedUsers, isLoading } = useAppSelector((state) => state.ListSubscribedUserSlice);

    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [filterType, setFilterType] = useState<string>("all");

    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterClose = (type?: string) => {
        if (type) setFilterType(type);
        setFilterAnchorEl(null);
    };

    useEffect(() => {
        dispatch(listSubscribedUser());
    }, [dispatch]);

    // 2. Columns Definition
    const columns: Columns = [
        {
            field: "index",
            renderHeader: () => <b>S.No</b>,
            width: 70,
            headerAlign: "center",
            align: "center",
            valueGetter: (params: any) => params.row.index,
        },
        {
            field: "username",
            renderHeader: () => <b>Username</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "email",
            renderHeader: () => <b>Email</b>,
            flex: 1.5,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "phone_no",
            renderHeader: () => <b>Contact No</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "year_group",
            renderHeader: () => <b>Year Group</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "package_name",
            renderHeader: () => <b>Package</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "subscribed_on",
            renderHeader: () => <b>Start Date</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "end_date",
            renderHeader: () => <b>End Date</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "pending_days",
            renderHeader: () => <b>Pending Days</b>,
            flex: 1,
            type: "number",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "amount",
            renderHeader: () => <b>Price</b>,
            flex: 1,
            type: "number",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "status",
            renderHeader: () => <b>Status</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
            cellClassName: (params) =>
                `activename ${params.row.status ? "active" : "inactive"}`,
            valueFormatter: (params) => (params.value ? "Active" : "Inactive"),
        },
    ];

    // Prepare rows with index and flatten data for grid functionality (sorting/filtering)
    const dataToMap = subscribedUsers?.data || [];
    const rows = dataToMap.map((item: any, idx: number) => {
        const today = dayjs();
        const endDate = dayjs(item.end_date);
        const diffInDays = endDate.diff(today, "day");

        return {
            ...item,
            id: item.sub_id,
            index: idx + 1,
            username: item.user_details?.username || "-",
            email: item.user_details?.email || "-",
            phone_no: item.user_details?.phone_no || "-",
            year_group: item.user_details?.year_group || "-",
            package_name: item.package_details?.name || "-",
            subscribed_on: item.subscribed_on ? dayjs(item.subscribed_on).format("DD-MM-YY") : "-",
            raw_subscribed_on: item.subscribed_on,
            end_date: item.end_date ? dayjs(item.end_date).format("DD-MM-YY") : "-",
            amount: item.price_details?.amount || 0,
            pending_days: diffInDays > 0 ? diffInDays : 0,
        };
    });

    const filteredRows = rows.filter(row => {
        if (filterType === "manual") return row.manual_assignment === true;
        if (filterType === "stripe") return !row.manual_assignment;
        return true;
    });

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Table
                rows={filteredRows}
                columns={columns}
                getRowClassName={(params) => {
                    const isToday = params.row.raw_subscribed_on && dayjs(params.row.raw_subscribed_on).isSame(dayjs(), 'day');
                    return isToday ? 'highlighted-row' : '';
                }}
            >
                <CustomToolbar>
                    <div style={{ marginRight: 'auto' }}>
                        {/* Empty spacer or Title if needed */}
                    </div>
                    <IconButton
                        onClick={handleFilterClick}
                        sx={{
                            mr: 1,
                            backgroundColor: filterType !== 'all' ? '#009ddf' : 'none',
                            color: filterType !== 'all' ? '#ffff' : 'black',
                            borderRadius: '8px',
                            padding: '6px',
                            '&:hover': {
                                backgroundColor: filterType !== 'all' ? '#bbdefb' : '#eeeeee',
                            }
                        }}
                    >
                        <FilterAltIcon />
                    </IconButton>
                    <Menu
                        anchorEl={filterAnchorEl}
                        open={Boolean(filterAnchorEl)}
                        onClose={() => handleFilterClose()}
                    >
                        <MenuItem onClick={() => handleFilterClose("all")} selected={filterType === "all"}>All</MenuItem>
                        <MenuItem onClick={() => handleFilterClose("manual")} selected={filterType === "manual"}>Manual</MenuItem>
                        <MenuItem onClick={() => handleFilterClose("stripe")} selected={filterType === "stripe"}>Stripe</MenuItem>
                    </Menu>
                    <DownloadButton data={filteredRows} />
                    <TableSearch />
                </CustomToolbar>
            </Table>
        </>
    );
};

export default SubscribedUser;
